import type { ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { BookOpen, ExternalLink, Layers } from 'lucide-react'
import {
  combineQueryStates,
  LinkChip,
  NotFoundView,
  PageHeader,
  QueryErrorPanel,
  safeHttpUrl,
  SectionHeader,
  Tag,
} from '@loop3/ui'
import {
  classifySolutionId,
  type SolutionDetail,
  type SolutionRef,
} from '@loop3/universe'
import { AskLupeButton } from '@/components/ask-lupe-button'
import { DetailSkeleton } from '@/components/explorer-detail'
import { EntityGrid } from '@/components/entity-card'
import { useSolution } from '@/hooks/useSolution'
import { solutionPath } from '@/lib/routes'
import type { Entity } from '@/lib/types/universe'

const cards = (refs: SolutionRef[]): Entity[] =>
  refs.map((r) => ({ kind: 'solution', id: r.id, title: r.name }))

function SolutionNotFound() {
  return (
    <NotFoundView
      title="Solution not found"
      description="No solution in the repository has this id."
      homeLink={<Link to="/explorer?kind=solution" />}
      homeLabel="Back to Explorer"
    />
  )
}

function Story({ title, text }: { title: string; text: string }) {
  if (!text.trim()) return null
  return (
    <section className="mb-8">
      <SectionHeader icon={BookOpen} label={title} />
      <p className="whitespace-pre-line text-sm leading-relaxed">{text}</p>
    </section>
  )
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="border-b border-border py-3 last:border-0">
      <div className="eyebrow mb-1.5 opacity-60">{label}</div>
      <div className="text-sm">{children}</div>
    </div>
  )
}

function RefList({ refs }: { refs: SolutionRef[] }) {
  return (
    <ul className="space-y-1">
      {refs.map((r) => (
        <li key={r.id}>
          <Link
            className="hover:text-[var(--l3-violet)]"
            to={solutionPath(r.id)}
          >
            {r.name}
          </Link>
        </li>
      ))}
    </ul>
  )
}

function Facts({ s }: { s: SolutionDetail }) {
  const status = [s.lifecycle, s.size && `Size ${s.size}`]
    .filter(Boolean)
    .join(' · ')
  return (
    <aside className="h-fit rounded-xl border border-border bg-card px-4">
      {s.client && (
        <Fact label="Client">
          <Link
            className="hover:text-[var(--l3-violet)]"
            to={`/clients/${s.client.prefix}`}
          >
            {s.client.name}
          </Link>
        </Fact>
      )}
      {status && <Fact label="Status">{status}</Fact>}
      {s.stages.length > 0 && (
        <Fact label="Knowledge loop">
          <div className="flex flex-wrap gap-1.5">
            {s.stages.map((st) => (
              <Tag key={st}>{st}</Tag>
            ))}
          </div>
        </Fact>
      )}
      {s.capabilities.length > 0 && (
        <Fact label="Capabilities">
          <div className="flex flex-wrap gap-1.5">
            {s.capabilities.map((c) => (
              <Tag key={c}>{c}</Tag>
            ))}
          </div>
        </Fact>
      )}
      {s.tools.length > 0 && <Fact label="Stack">{s.tools.join(', ')}</Fact>}
      {s.people.length > 0 && (
        <Fact label="People">
          <ul className="space-y-1">
            {s.people.map((p) => (
              <li key={p.name} className={p.current ? '' : 'opacity-60'}>
                {p.personId ? (
                  <Link
                    className="hover:text-[var(--l3-violet)]"
                    to={`/explorer/people/${p.personId}`}
                  >
                    {p.name}
                  </Link>
                ) : (
                  p.name
                )}
                {!p.current && ' (former)'}
              </li>
            ))}
          </ul>
        </Fact>
      )}
      {s.partOf.length > 0 && (
        <Fact label="Part of">
          <RefList refs={s.partOf} />
        </Fact>
      )}
      {s.dependsOn.length > 0 && (
        <Fact label="Depends on">
          <RefList refs={s.dependsOn} />
        </Fact>
      )}
      {s.derivedFrom.length > 0 && (
        <Fact label="Derived from">
          <RefList refs={s.derivedFrom} />
        </Fact>
      )}
      {s.supersedes.length > 0 && (
        <Fact label="Supersedes">
          <RefList refs={s.supersedes} />
        </Fact>
      )}
      {s.related.length > 0 && (
        <Fact label="Related">
          <RefList refs={s.related} />
        </Fact>
      )}
    </aside>
  )
}

/** One solution from the repository: the story on the left, facts on the right. */
export function SolutionPage() {
  const { id } = useParams<{ id: string }>()
  const valid = !!id && classifySolutionId(id) !== null
  const solution = useSolution(id)
  const { status, retry } = combineQueryStates([solution])

  if (!valid) return <SolutionNotFound />
  if (status === 'error')
    return (
      <div className="mx-auto max-w-7xl pb-16">
        <QueryErrorPanel onRetry={retry} />
      </div>
    )
  if (status === 'pending') return <DetailSkeleton />
  if (!solution.data) return <SolutionNotFound />

  const s = solution.data
  const notion = safeHttpUrl(s.notionUrl)
  return (
    <div className="mx-auto max-w-7xl pb-16">
      <PageHeader
        className="mt-2"
        eyebrow={s.type === 'feature' ? 'Feature' : 'Solution'}
        title={<span className="[overflow-wrap:anywhere]">{s.name}</span>}
        actions={<AskLupeButton question={`Tell me about ${s.name}`} />}
      >
        {s.summary && (
          <p className="mt-2 max-w-3xl text-sm opacity-80">{s.summary}</p>
        )}
        {notion && (
          <div className="mt-4">
            <LinkChip
              href={notion}
              icon={
                <ExternalLink
                  className="size-3.5 shrink-0 opacity-60"
                  aria-hidden
                />
              }
              label="Edit in Notion"
            />
          </div>
        )}
      </PageHeader>

      {s.archived && (
        <p className="mb-6 rounded-xl border border-border p-3 text-sm opacity-70">
          This solution is no longer in the repository. It is kept here so links
          keep working.
        </p>
      )}

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_18rem]">
        <div className="min-w-0">
          <Story title="Problem" text={s.problem} />
          <Story title="Solution" text={s.approach} />
          <Story title="Gains" text={s.gains} />
          <Story title="Learnings" text={s.learnings} />
          {s.features.length > 0 && (
            <section className="mb-8">
              <SectionHeader
                icon={Layers}
                label="Features"
                meta={s.features.length}
              />
              <EntityGrid
                items={cards(s.features)}
                className="sm:grid-cols-2"
              />
            </section>
          )}
        </div>
        <Facts s={s} />
      </div>
    </div>
  )
}
